import React, { createContext, useContext, useState, useEffect } from 'react';
import { auth, db, googleProvider } from '../firebase';
import { UserProfile } from '../types';
import { useToast } from '../components/ui/Toast';

interface AuthContextType {
  user: UserProfile | null;
  loading: boolean; // isAuthChecking
  login: () => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const { addToast } = useToast();

  useEffect(() => {
    let unsubscribeSnapshot: (() => void) | null = null;

    const unsubscribeAuth = auth.onAuthStateChanged(async (authUser: any) => {
      // Clean up previous snapshot listener if exists
      if (unsubscribeSnapshot) {
          unsubscribeSnapshot();
          unsubscribeSnapshot = null;
      }

      if (authUser) {
        try {
          const userRef = db.collection('users').doc(authUser.uid);
          const snap = await userRef.get();
          
          if (!snap.exists) {
              await userRef.set({
                  email: authUser.email,
                  displayName: authUser.displayName,
                  photoURL: authUser.photoURL,
                  credits: 50,
                  createdAt: new Date()
              });
          }
          
          // Set up real-time listener
          unsubscribeSnapshot = userRef.onSnapshot((docSnap: any) => {
            if (docSnap.exists) {
              const data = docSnap.data();
              setUser({
                uid: authUser.uid,
                email: authUser.email,
                displayName: authUser.displayName,
                photoURL: authUser.photoURL,
                credits: data?.credits || 0
              });
            } else {
               setUser({
                uid: authUser.uid,
                email: authUser.email,
                displayName: authUser.displayName,
                photoURL: authUser.photoURL,
                credits: 0
              });
            }
            setLoading(false);
          }, (error: any) => {
              console.error("Snapshot Error:", error);
              setUser(null);
              setLoading(false);
          });
          
        } catch (error: any) {
          console.error("Login Flow Error:", error);
          setUser(null);
          setLoading(false);
        }
      } else {
        setUser(null);
        setLoading(false);
      }
    });
    
    return () => {
        unsubscribeAuth();
        if (unsubscribeSnapshot) unsubscribeSnapshot();
    };
  }, []);

  const login = async () => {
    try {
      await auth.signInWithPopup(googleProvider);
    } catch (error: any) {
      console.error("Login Error:", error);
      addToast(error.message, 'error');
      throw error;
    }
  };

  const logout = async () => {
    try {
      await auth.signOut();
      setUser(null);
    } catch (error: any) {
      console.error("Logout Error:", error);
      addToast(error.message, 'error');
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
